import React from "react";

import {
  BarChart,
  Delete,
  Edit,
  FileDownload,
  LinkRounded,
  MoreVert,
  PeopleAlt,
  PieChart,
  Place,
  Share,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { Audience, AudienceActions } from "@/types";
import { abbreviateNumber, reformatDate } from "@/utils";

interface AudienceListItemProps {
  audience: Audience;
  actions: AudienceActions;
}

const AudienceListItem: React.FC<AudienceListItemProps> = ({
  audience,
  actions
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isSharedAudience = audience.shared;
  const numSharedWith =
    (audience.shared_with && audience.shared_with.length) || 0;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditAudience = () => {
    actions.editAudience(audience);
    handleMenuClose();
  };

  const handleDeleteAudience = () => {
    actions.deleteAudience(audience);
    handleMenuClose();
  };

  const handleShareAudience = () => {
    actions.shareAudience(audience);
    handleMenuClose();
  };

  const handleViewInsights = () => {
    actions.viewInsights(audience);
    handleMenuClose();
  };

  const handleExportAudience = () => {
    actions.exportAudience(audience);
    handleMenuClose();
  };

  const getAudienceSize = () => {
    if (audience.audience_size && audience.audience_size.length > 1) {
      return `${audience.audience_size.length} segments`;
    } else if (
      audience.audience_size &&
      audience.audience_size[0].population_count
    ) {
      return abbreviateNumber(audience.audience_size[0].population_count);
    }
    return "0";
  };

  const getPostcodesCount = () => {
    if (audience.audience_size && audience.audience_size[0].num_postcodes) {
      return abbreviateNumber(audience.audience_size[0].num_postcodes);
    }
    return "0";
  };

  const sharedOverlay = alpha(theme.palette.primary.main, 0.051);
  const paperOverlay = alpha("#fff", 0.051);
  const paperShadow = "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)"
  return (
    <TableRow
      hover
      onClick={() => actions.selectAudience(audience)}
      sx={{
        cursor: "pointer",
        backgroundColor: isSharedAudience
          ? sharedOverlay
          : paperOverlay,
        boxShadow: paperShadow,
        "& td": {
          borderBottom: "none",
        },
        borderRadius: "6px",
      }}
    >
      <TableCell sx={{ borderRadius: "6px 0px 0px 6px" }}>
        <Stack direction="row" spacing={1} display="flex" alignItems="center">
          {audience.status === 'processing' && (
            <HelpBox title="Audience being processed">
              <CircularProgress size={20} color="info"/>
            </HelpBox>
          )}
          <Typography
            variant="body1"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: { xs: "128px", sm: "128px", lg: "300px" },
            }}
          >
            {audience.audience_name}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: { xs: "80px", sm: "80px", lg: "200px" },
          }}
        >
          {audience.project_name || "—"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {audience.created_at ? reformatDate(audience.created_at) : "—"}
        </Typography>
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={0.5}>
          {audience.audience_size && audience.audience_size.length > 1 ?
            <PieChart fontSize="small" color="action" />
          :
            <PeopleAlt fontSize="small" color="action" />
          }
          <Typography variant="body2">{getAudienceSize()}</Typography>
        </Box>
      </TableCell>
      {/*<TableCell>*/}
      {/*  <Box display="flex" alignItems="center" gap={0.5}>*/}
      {/*    <Place fontSize="small" color="action" />*/}
      {/*    <Typography variant="body2">{getPostcodesCount()}</Typography>*/}
      {/*  </Box>*/}
      {/*</TableCell>*/}
      <TableCell>
        <Box display="flex" justifyContent="center">
          {isSharedAudience ? (
            <HelpBox title={"This audience is shared with you"}>
              <LinkRounded color="primary" fontSize="small" />
            </HelpBox>
          ) : audience.shared_with && numSharedWith > 0 ? (
            <HelpBox
              title={`This audience is shared with ${numSharedWith} user${
                numSharedWith > 1 ? "s" : ""
              }`}
            >
              <Avatar
                variant="rounded"
                sx={{
                  background: alpha(theme.palette.info.main, 0.5),
                  width: 20,
                  height: 20,
                  fontSize: "12px",
                }}
              >
                {numSharedWith}
              </Avatar>
            </HelpBox>
          ) : null}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ pl: 0, borderRadius: "0px 6px 6px 0px" }}>
        <IconButton size="small" onClick={handleMenuClick}>
          <MoreVert />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleEditAudience} disabled={audience.shared}>
            <Edit fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleViewInsights}>
            <BarChart fontSize="small" sx={{ mr: 1 }} />
            View Insight
          </MenuItem>
          <MenuItem onClick={handleExportAudience}>
            <FileDownload fontSize="small" sx={{ mr: 1 }} />
            Export
          </MenuItem>
          <MenuItem onClick={handleShareAudience} disabled={audience.shared}>
            <Share fontSize="small" sx={{ mr: 1 }} />
            Share
          </MenuItem>
          <MenuItem
            onClick={handleDeleteAudience}
            disabled={audience.shared}
            sx={{ color: "error.main" }}
          >
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export default AudienceListItem;
